import AttributeCondition from 'cio/app-models/conditions/attribute';
import EventAttributeCondition from 'cio/app-models/conditions/event-attribute';
import FormEventAttributeCondition from 'cio/app-models/conditions/form-event-attribute';
import CollectionAttributeCondition from 'cio/app-models/conditions/collection-attribute';
import {
  ago,
  fromNow,
  isBinaryOperator,
  isRelativeTimestampOperator,
  isTwoValueOperator,
} from 'cio/app-models/conditions/attribute';
import { SECONDS_IN_DAY } from 'cio/utils/constants';
import { SerializedAttributeCondition } from 'cio/types/conditions/condition';
import { SerializedLegacyAttributeFilter } from 'cio/types/conditions/legacy-filter';
import DeviceAttributeCondition from 'cio/app-models/conditions/device-attribute';
import ObjectAttributeCondition, { ObjectAttributeSource } from 'cio/app-models/conditions/object-attribute';
import { ConditionType } from 'cio/utils/condition-types';

export function deserializeFilter(filter: SerializedLegacyAttributeFilter) {
  return createAttribute(
    filter.field,
    filter.operator,
    filter.comparison_operator,
    filter.value,
    filter.value2,
    filter.inverse,
    filter.event,
    filter.type,
    filter.collectionSchema,
    filter.form_type,
    filter.isForeignEventFilterAttribute,
    filter.isConversionEventAttribute,
    filter.type_id,
    filter.objectAttributeSource,
  );
}

type OptionalString = string | null | undefined;
export type AttributeConditionRecord = Record<string, unknown>;

export function createAttribute(
  name: string,
  operator: string,
  comparisonOperator: string | undefined,
  value: OptionalString,
  value2: OptionalString,
  inverse: boolean | undefined,
  event: unknown,
  type: string | undefined,
  collectionSchema: OptionalString,
  formType: OptionalString,
  isForeignEventFilterAttribute?: boolean,
  isConversionEventAttribute?: boolean,
  objectTypeId?: number,
  objectAttributeSource?: ObjectAttributeSource,
  factoryFn: (attributes: AttributeConditionRecord) => AttributeCondition = (attr) => AttributeCondition.create(attr),
):
  | DeviceAttributeCondition
  | FormEventAttributeCondition
  | EventAttributeCondition
  | ObjectAttributeCondition
  | CollectionAttributeCondition
  | AttributeCondition {
  let attributes: AttributeConditionRecord = { name };
  if (inverse) {
    operator = `!${operator}`;
  }

  attributes.operator = operator;

  if (comparisonOperator) {
    attributes.comparisonOperator = comparisonOperator;
  }

  if (isRelativeTimestampOperator(operator)) {
    let parsedValue = parseInt(value as string) || 0;
    parsedValue = Math.floor(parsedValue / SECONDS_IN_DAY);

    attributes.timestampValue = Math.abs(parsedValue).toString();
    attributes.timeDirection = parsedValue < 0 ? ago : fromNow;
  } else if (isTwoValueOperator(operator)) {
    attributes.value = value;
    attributes.value2 = value2;
  } else if (isBinaryOperator(operator)) {
    attributes.value = value;
  }

  if (event) {
    attributes.event = event;
  }

  if (isForeignEventFilterAttribute) {
    attributes.isForeignEventFilterAttribute = isForeignEventFilterAttribute;
  }

  if (isConversionEventAttribute) {
    attributes.isConversionEventAttribute = isConversionEventAttribute;
  }

  if (collectionSchema) {
    attributes.collectionSchema = collectionSchema;
  }

  if (formType) {
    attributes.formType = formType;
  }

  if (objectTypeId) {
    attributes.objectTypeId = String(objectTypeId);
  }

  if (objectAttributeSource) {
    attributes.objectAttributeSource = objectAttributeSource;
  }

  if (type === 'event_attribute' && formType) {
    return FormEventAttributeCondition.create(attributes);
  } else if (type === 'event_attribute') {
    return EventAttributeCondition.create(attributes);
  } else if (type === 'collection_attribute') {
    return CollectionAttributeCondition.create(attributes);
  } else if (type === 'device_attribute') {
    return DeviceAttributeCondition.create(attributes);
  } else if ([ConditionType.ObjectAttribute, ConditionType.RelationshipAttribute].includes(type as ConditionType)) {
    if (type === ConditionType.RelationshipAttribute) {
      attributes.type = ConditionType.RelationshipAttribute;
    }
    return ObjectAttributeCondition.create(attributes);
  }

  return factoryFn(attributes);
}

export default function (json: SerializedAttributeCondition) {
  let { event } = json;
  let name = event?.name;
  let type = event?.type;
  let filter = event?.filters?.and?.[0];
  let { operator, comparison_operator, value, value2, inverse, form_type, type_id } = filter || {};

  return createAttribute(
    name as string,
    operator as string,
    comparison_operator as string,
    value as string,
    value2,
    inverse as boolean,
    null,
    type,
    null,
    form_type,
    false,
    false,
    type_id,
  );
}
